import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SEOHead } from '../components/SEOHead';
import { ProjectGridSkeleton } from '../components/Skeleton';
import { useProjects } from '../hooks/useProjects';
import { useEditor } from '../contexts/EditorContext';
import { InlineText } from '../components/InlineText';

export default function ProjektePage() {
  const { getValue } = useEditor();
  const e = getValue || ((_, fb) => fb);
  const { projects, categories, loading } = useProjects();
  const [activeCategory, setActiveCategory] = useState('alle');

  const filteredProjects = useMemo(() => {
    if (activeCategory === 'alle') return projects;
    return projects.filter(p => p.category === activeCategory);
  }, [activeCategory, projects]);

  return (
    <div data-testid="projekte-page" className="overflow-hidden">
      <SEOHead page="projekte" />

      <section className="relative py-24 md:py-32 lg:py-40">
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-6 md:px-12 lg:px-16">
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mb-12 md:mb-20"
          >
            <InlineText as="p" cmsKey="projekte.heroLabel" fallback="Portfolio" className="text-[10px] sm:text-[11px] font-medium text-[#9A9A9A] uppercase tracking-[0.15em] mb-4 sm:mb-6" />
            <InlineText as="h1" cmsKey="projekte.heroTitle" fallback="Projekte & Referenzen" data-testid="projekte-hero-title" className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-light tracking-[-0.04em] leading-[1] sm:leading-[0.95] mb-4 sm:mb-6 text-[#1A1A1A]" />
            <InlineText as="p" cmsKey="projekte.heroSubline" fallback="Kuratiert. Präzise. Umgesetzt." className="text-sm sm:text-base md:text-lg text-[#6B6B6B] font-light" />
          </motion.header>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10 md:mb-16"
          >
            <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full max-w-2xl">
              <TabsList className="w-full flex-wrap h-auto gap-1.5 sm:gap-2 p-1.5 sm:p-2">
                {categories.map((cat) => (
                  <TabsTrigger key={cat.value} value={cat.value} data-testid={`filter-${cat.value}`} className="flex-1 min-w-[65px] sm:min-w-[80px] text-xs sm:text-sm py-1.5 sm:py-2">
                    {cat.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </motion.div>

          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div key="skeleton" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                <ProjectGridSkeleton count={6} />
              </motion.div>
            ) : (
              <motion.div
                key={activeCategory}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
                role="list"
                aria-label="Projektliste"
              >
                {filteredProjects.map((project, index) => (
                  <motion.article
                    key={project.id || project.slug}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    role="listitem"
                  >
                    <Link to={`/projekte/${project.slug}`}>
                      <div
                        data-testid={`project-card-${project.slug}`}
                        className="group relative aspect-[4/3] overflow-hidden bg-[#E8E4DD] border border-black/[0.06] hover:border-black/[0.12] transition-all duration-500 cursor-pointer"
                      >
                        {project.coverImage?.url && (
                          <img
                            src={project.coverImage.url}
                            alt={project.coverImage.alt || project.title}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent group-hover:via-black/25 transition-all duration-700" />
                        <div className="absolute inset-0 flex flex-col justify-end p-5 sm:p-6 z-10">
                          <span className="text-[10px] sm:text-[11px] font-medium text-white/70 uppercase tracking-[0.1em] mb-1.5 sm:mb-2">
                            {project.category}
                          </span>
                          <h2 className="text-base sm:text-lg font-light tracking-[-0.01em] mb-1.5 sm:mb-2 text-white leading-snug">{project.title}</h2>
                          <p className="text-[12px] sm:text-[13px] text-white/60 mb-2 sm:mb-3 line-clamp-2 leading-relaxed">{project.shortDescription}</p>
                          <div className="flex flex-wrap gap-1.5 sm:gap-2">
                            {(project.tags || []).slice(0, 3).map((tag) => (
                              <span key={tag} className="px-2.5 py-1 text-[11px] text-white/70 border border-white/25">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <ArrowRight className="w-5 h-5 text-white/70" />
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 md:py-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden border border-black/[0.06] p-12 md:p-20 lg:p-28"
          >
            <div className="relative max-w-2xl">
              <InlineText as="p" cmsKey="projekte.ctaLabel" fallback="Jetzt starten" className="text-[11px] font-medium text-[#9A9A9A] uppercase tracking-[0.15em] mb-5" />
              <InlineText as="h2" cmsKey="projekte.ctaTitle" fallback="Ihr Projekt als nächstes?" className="text-3xl md:text-4xl lg:text-5xl font-light tracking-[-0.03em] leading-[1.1] mb-8 text-[#1A1A1A]" />
              <InlineText as="p" cmsKey="projekte.ctaDesc" fallback="Lassen Sie uns über Ihre Anforderungen sprechen – unverbindlich und persönlich." className="text-base text-[#6B6B6B] mb-12 leading-relaxed font-light max-w-lg" />
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/kontakt"><Button>{e('projekte.ctaButtonLabel', 'Projekt besprechen')}<ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
                <Link to="/kontakt"><Button variant="secondary">{e('projekte.ctaButton2Label', 'Angebot anfordern')}</Button></Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
